import { vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../utils/uniforms/UniformVec3';
import { Renderer } from '../Renderer';
import { crossSectionVertexShader } from './glsl';
import {
    ADDED_COLOR,
    computeSliceZStep,
    DIFF_FILL_ALPHA,
    REMOVED_COLOR,
} from './util/crossSliceDiff';

/**
 * Renders the cross-slice diff overlay (MIM parity §5.9.4) for a
 * single ROI's SDF: on the current slice, fills the pixels where the
 * contour differs from the previous acquisition slice — green where
 * area was added, red where it was removed.
 *
 * This is the "two SDF-iso renderings XOR'd" described in the design
 * sprint. It reuses the same view-plane cross-section of the 3D SDF as
 * {@link ContourRenderer}, but samples the field twice: once at the
 * fragment and once one image slice back along the texture's Z
 * (acquisition-stacking) axis. Because that axis is `textureCoords.z`
 * in every view, the overlay is correct in transversal, coronal and
 * sagittal without any orientation-specific handling.
 *
 * Display aid only: it reads the SDF and never writes it.
 */
export class CrossSliceDiffRenderer implements Renderer {
    private readonly shader: CrossSliceDiffShader;

    public constructor() {
        this.shader = new CrossSliceDiffShader();
    }

    /**
     * Draw the diff overlay for a single contour texture on a
     * {@link PlaneView}. The caller must resolve `texture` (which may
     * trigger lazy SDF generation) before invoking this, so no GL
     * program switch can occur once the shader is bound.
     */
    public render(view: TextureView, texture: ContourTexture): void {
        if (!(view instanceof PlaneView)) {
            return;
        }

        const vao = texture.crossSection(view.projectionViewMatrix);
        if (!vao) {
            return;
        }
        try {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            this.shader.start();

            const imageUvRange = texture.getImageUvRange();
            const imageZRange = vec2.fromValues(
                imageUvRange.min[2],
                imageUvRange.max[2]
            );
            this.shader.imageZRange.loadVec2(imageZRange);

            // UV-Z offset to the previous acquisition slice's iso-contour.
            const sliceZStep = computeSliceZStep(
                imageZRange[0],
                imageZRange[1],
                texture.grid.sizeZ
            );
            this.shader.sliceZStep.loadFloat(sliceZStep);

            this.shader.addedColor.loadVec3(ADDED_COLOR);
            this.shader.removedColor.loadVec3(REMOVED_COLOR);
            this.shader.fillAlpha.loadFloat(DIFF_FILL_ALPHA);

            texture.bindTexture();
            vao.bind(0, 1);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        } finally {
            vao.unbind(0, 1);
            vao.delete();
            texture.unbindTexture();
            this.shader.stop();
            gl.disable(gl.BLEND);
        }
    }

    public delete(): void {
        this.shader.delete();
    }
}

/**
 * Shader that classifies each fragment inside/outside the SDF iso at
 * the current texture-UV and at the same UV shifted one image slice
 * back along the texture's Z axis. The symmetric difference of the two
 * classifications is filled: added (current only) or removed (previous
 * only); matching or empty fragments are discarded.
 */
class CrossSliceDiffShader extends ShaderProgram {
    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;
    out vec4 outColor;

    uniform sampler3D sampler;
    // Texture-UV Z range corresponding to the image grid's first and
    // last slice center positions.
    uniform vec2 imageZRange;
    // Texture-UV Z distance between two adjacent image slice centers.
    uniform float sliceZStep;
    uniform vec3 addedColor;
    uniform vec3 removedColor;
    uniform float fillAlpha;

    const float SDF_THRESHOLD = 0.5;
    // Matches ContourRenderer's Z clip tolerance (see its shader for
    // the rationale): the uniform is float32 while the cross-section's
    // UV z arrives via a different arithmetic path. Mirrors Z_CLIP_EPSILON
    // in crossSliceDiff.ts (golden-tested; keep the two in sync).
    const float Z_CLIP_EPSILON = 1.0 / 100000.0;

    void main() {
        // Hard clip in Z at the image's first and last slice center
        // positions, as ContourRenderer does — the cross-section
        // geometry extends a half-voxel past each clinical end.
        float z = pass_textureCoordinates.z;
        if (z + Z_CLIP_EPSILON < imageZRange.x ||
            z > imageZRange.y + Z_CLIP_EPSILON) {
            discard;
        }

        bool insideCurrent =
            texture(sampler, pass_textureCoordinates).r >= SDF_THRESHOLD;

        // Previous acquisition slice, one step back along texture Z.
        // Off the bottom of the stack there is no previous slice, so
        // treat it as empty: the whole current contour then reads as
        // added, which is the correct "nothing before it" behaviour.
        //
        // The lower bound is the max of the image's first slice center
        // (no acquisition slice exists before it) and texture UV 0:
        // imageZRange.x may be negative when the SDF grid sits strictly
        // inside the image, and below UV 0 the ROI has no contour by
        // construction — but sampling there would CLAMP_TO_EDGE onto
        // the bottom texel row, so it must be skipped, not sampled.
        // This priorMinZ clamp mirrors hasPreviousSlice() in crossSliceDiff.ts
        // (golden-tested; keep the two in sync).
        vec3 priorPos = vec3(pass_textureCoordinates.xy, z - sliceZStep);
        bool insidePrior = false;
        float priorMinZ = max(imageZRange.x, 0.0);
        if (priorPos.z + Z_CLIP_EPSILON >= priorMinZ) {
            insidePrior = texture(sampler, priorPos).r >= SDF_THRESHOLD;
        }

        // Symmetric-difference classification — mirrors
        // classifyCrossSliceDiff() in crossSliceDiff.ts (golden-tested;
        // keep the two branches in sync).
        if (insideCurrent && !insidePrior) {
            outColor = vec4(addedColor, fillAlpha);
        } else if (!insideCurrent && insidePrior) {
            outColor = vec4(removedColor, fillAlpha);
        } else {
            discard;
        }
    }`;

    public readonly imageZRange: UniformVec2;
    public readonly sliceZStep: UniformFloat;
    public readonly addedColor: UniformVec3;
    public readonly removedColor: UniformVec3;
    public readonly fillAlpha: UniformFloat;

    constructor() {
        super(
            crossSectionVertexShader,
            CrossSliceDiffShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.imageZRange = new UniformVec2('imageZRange');
        this.sliceZStep = new UniformFloat('sliceZStep');
        this.addedColor = new UniformVec3('addedColor');
        this.removedColor = new UniformVec3('removedColor');
        this.fillAlpha = new UniformFloat('fillAlpha');
        this.storeUniformLocations(
            this.imageZRange,
            this.sliceZStep,
            this.addedColor,
            this.removedColor,
            this.fillAlpha
        );
    }
}
